import { withAlpha } from "@nexus/widget-sdk";
import type { ChartColors } from "@nexus/widget-sdk";

export interface GroupColor {
  /** CSS colour for text in the DOM. */
  css: string;
  /** Canvas-safe colour for ECharts. */
  chart: (colors: ChartColors) => string;
}

/** Fixed hues so a network group reads the same in every instance of a dashboard. */
const FIXED_FAMILIES: Record<string, GroupColor> = {
  OTT: { css: "var(--brand-cyan)", chart: (colors) => colors.accent },
  IPTV: { css: "var(--data-response)", chart: (colors) => colors.operational.response },
  DTH: { css: "var(--data-anomaly)", chart: (colors) => colors.anomaly },
};

/** Sub-levels with a reserved shade, so they keep it whichever others are present. */
const KNOWN_SUB_LEVELS: Record<string, readonly string[]> = {
  IPTV: ["SFR", "FREE", "CONTRIB"],
};

/** Shades of a family: no sub-level first, then deeper and lighter variants. */
const SHADES: readonly { toward: "white" | "black"; percent: number }[] = [
  { toward: "white", percent: 0 },
  { toward: "black", percent: 25 },
  { toward: "white", percent: 35 },
  { toward: "white", percent: 60 },
  { toward: "white", percent: 15 },
  { toward: "black", percent: 40 },
];

const PALETTE_SIZE = 6;

/** Mixes an `rgb()`/`rgba()` colour toward white or black, like CSS `color-mix(in srgb)`. */
function mixRgb(color: string, toward: "white" | "black", percent: number): string {
  const match = color.match(/rgba?\(([^)]+)\)/);
  if (!match) return color;
  const [r, g, b, a] = match[1].split(",").map((part) => Number.parseFloat(part));
  const target = toward === "white" ? 255 : 0;
  const mix = (channel: number) => Math.round(channel + (target - channel) * (percent / 100));
  return a === undefined
    ? `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`
    : `rgba(${mix(r)}, ${mix(g)}, ${mix(b)}, ${a})`;
}

function shade(base: GroupColor, index: number): GroupColor {
  const { toward, percent } = SHADES[index % SHADES.length];
  if (percent === 0) return base;
  return {
    css: `color-mix(in srgb, ${base.css}, ${toward} ${percent}%)`,
    chart: (colors) => mixRgb(base.chart(colors), toward, percent),
  };
}

function paletteColor(position: number): GroupColor {
  return {
    css: `var(--chart-${(position % PALETTE_SIZE) + 1})`,
    chart: (colors) => {
      const base = colors.series[position % colors.series.length];
      return position >= PALETTE_SIZE ? withAlpha(base, 0.55) : base;
    },
  };
}

/**
 * OTT, IPTV and DTH keep their brand hue, other network groups take the
 * categorical palette in rank order, and sub-levels are shades of their group's hue.
 */
export function groupColors(
  labels: readonly string[],
  families: ReadonlyMap<string, { group: string; subLevel: string | null }>,
): Map<string, GroupColor> {
  const parts = labels.map((label) => {
    const { group, subLevel } = families.get(label) ?? { group: label, subLevel: null };
    return { label, family: group.trim().toUpperCase(), sub: subLevel?.trim().toUpperCase() };
  });

  const bases = new Map<string, GroupColor>();
  let nextPalette = 0;
  for (const { family } of parts)
    if (!bases.has(family))
      bases.set(family, FIXED_FAMILIES[family] ?? paletteColor(nextPalette++));

  return new Map(
    parts.map(({ label, family, sub }) => {
      const base = bases.get(family) ?? paletteColor(0);
      if (!sub) return [label, base];
      const known = KNOWN_SUB_LEVELS[family] ?? [];
      const unknown = [
        ...new Set(
          parts
            .filter((part) => part.family === family && part.sub && !known.includes(part.sub))
            .map((part) => part.sub as string),
        ),
      ].sort();
      const index = known.includes(sub)
        ? 1 + known.indexOf(sub)
        : 1 + known.length + unknown.indexOf(sub);
      return [label, shade(base, index)];
    }),
  );
}
